import { useCallback, useEffect, useState } from 'react'
import { useMutation } from '@connectrpc/connect-query'
import {
  federatedLogin as federatedLoginDescriptor,
  emailLogin as emailLoginDescriptor,
} from '@jotta/grpc-connect-openapi/esm/openapi/login/v1/login.v1-LoginService_connectquery'
import { Federation } from './Federation'
import { type Form as UseFormResult, useForm } from '@jotta/hooks'
import { Form } from '@jotta/ui/Form'
import { FormField } from '@jotta/ui/FormField'
import { PasswordInput } from '@jotta/ui/PasswordInput'
import { TextInput } from '@jotta/ui/TextInput'
import { CTAButton } from './CTAButton'
import { ErrorMessage } from '@jotta/ui/ErrorMessage'
import { generateSignupToken } from './signupForm'
import { useGetConfigSuspense } from '@jotta/grpc-connect-client/config'
import { t, Trans } from '@lingui/macro'
import { PreSignup } from './PreSignup'
import { Feature } from '@jotta/grpc-connect-openapi/feature'
import type { EmailLoginResponse } from '@jotta/grpc-connect-openapi/login'
import {
  EmailLoginRequest,
  FederatedLoginRequest,
} from '@jotta/grpc-connect-openapi/login'
import type { PartialMessage } from '@bufbuild/protobuf'
import type { Credentials } from '@jotta/grpc-connect-openapi/credentials'
import { Challenge } from '@jotta/grpc-connect-openapi/credentials'
import { ChallengeDialogModal } from '@jotta/ui/ChallengeDialog'
import { Redirect } from '@jotta/ui/Redirect'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { LoginExpired } from './LoginExpired'
import { ModalDialog } from '@jotta/ui/ModalDialog'
import { ResetPassword } from './ResetPassword'
import { FormFieldErrorMessage } from '@jotta/ui/Label'
import Spinner from '@jotta/ui/Spinner'
import { useAuthStore } from './useAuthStore'
import { OrDivider } from '@jotta/ui/Divider'
import { CreateUser } from './CreateUser'

export const restartLoginUrl = '/api/login?redirect_uri=/'

function LoginForm({
  form,
  federatedLoginError,
  showLinkedAccounts,
  wrongPassword,
  onSignupRequested,
}: {
  form: UseFormResult<any>
  federatedLoginError: Error | null
  showLinkedAccounts: boolean | undefined
  wrongPassword?: boolean
  onSignupRequested?: () => void
}) {
  const [forgotPassword, setForgotPassword] = useState(false)
  const { email, password } = form

  return (
    <>
      <ModalDialog
        open={forgotPassword}
        className="!px-8 !py-16"
        onClose={() => setForgotPassword(false)}
      >
        <ResetPassword onClose={() => setForgotPassword(false)} />
      </ModalDialog>

      <h1 className="body-lg-ln pb-12">
        <Trans>Log In</Trans>
      </h1>

      {showLinkedAccounts && (
        <>
          <Federation
            intent={{
              case: 'login',
              value: {},
            }}
          />
          <OrDivider />
        </>
      )}

      <ErrorMessage error={federatedLoginError} />

      <Form form={form} className="flex flex-col gap-4">
        <FormField field={email} label={t`Email`}>
          <TextInput {...email.inputProps} />
        </FormField>

        <FormField field={password} label={t`Password`}>
          <PasswordInput {...password.inputProps} />
          <button
            type="button"
            className="btn btn-link self-start pt-2 text-signup-link"
            onClick={event => {
              event.preventDefault()
              setForgotPassword(true)
            }}
          >
            <Trans>Forgot password?</Trans>
          </button>
          {wrongPassword && (
            <FormFieldErrorMessage errorMessage={t`Invalid password`} />
          )}
        </FormField>

        <CTAButton type="submit">
          <Trans>Login</Trans>
        </CTAButton>

        {onSignupRequested && (
          <p className="text-grey4">
            <Trans>Don't have an account?</Trans>
            &nbsp;
            <button
              className="!text-signup-link underline"
              onClick={event => {
                event.preventDefault()
                onSignupRequested()
              }}
            >
              <Trans>Sign up now</Trans>
            </button>
          </p>
        )}
      </Form>
    </>
  )
}

export function LoginPage({ loginToken }: { loginToken: string }) {
  const {
    data: { brandingInfo, activeFeatures, viewConfig },
  } = useGetConfigSuspense()
  const { federationToken } = useAuthStore()

  const showLinkedAccounts = viewConfig?.showLinkedAccounts

  const [signup, setSignup] = useState(false)
  const [emailLoginResponse, setEmailLoginResponse] =
    useState<EmailLoginResponse>()

  const { mutate: federatedLogin, error: federatedLoginError } = useMutation(
    federatedLoginDescriptor,
    { throwOnError: false },
  )

  const { mutateAsync: emailLogin, reset } = useMutation(emailLoginDescriptor, {
    onSuccess: setEmailLoginResponse,
    throwOnError: false,
  })

  const brandCode = brandingInfo?.brandCode

  const submitCredentials = useCallback(
    async (
      email: string | undefined,
      credentials: PartialMessage<Credentials>,
    ) =>
      emailLogin(
        await generateSignupToken(
          new EmailLoginRequest({
            brandCode,
            loginToken,
            email,
            credentials,
          }),
          (message, token) => (message.token = token),
        ),
      ),
    [emailLogin, brandCode, loginToken],
  )

  const form = useForm({
    fields: {
      email: {
        type: 'text',
      },
      password: {
        type: 'text',
      },
    },
    submit: async ({ email, password }) => {
      return submitCredentials(email, {
        password,
      })
    },
  })

  useEffect(() => {
    if (federationToken && !federatedLoginError) {
      generateSignupToken(
        new FederatedLoginRequest({
          brandCode,
          loginToken,
          federationToken,
        }),
        (message, token) => (message.token = token),
      ).then(value => federatedLogin(value))
    }
  }, [federationToken])

  if (federationToken && !federatedLoginError) {
    return <Spinner />
  }

  if (activeFeatures.includes(Feature.PRE_SIGNUP)) {
    return <PreSignup loginToken={loginToken} />
  }

  const signupSupported = activeFeatures.includes(Feature.SIGNUP)
  const voucherSignupSupported = activeFeatures.includes(Feature.VOUCHER_SIGNUP)

  if (signup) {
    return (
      <CreateUser
        type={signupSupported ? 'private' : 'voucher'}
        loginToken={loginToken}
        onClose={() => setSignup(false)}
        onRequestLogin={() => setSignup(false)}
      />
    )
  }

  const onSignupRequested =
    signupSupported || voucherSignupSupported
      ? () => setSignup(true)
      : undefined

  const result = emailLoginResponse?.result
  switch (result?.case) {
    case undefined:
      return (
        <LoginForm
          form={form}
          showLinkedAccounts={showLinkedAccounts}
          federatedLoginError={federatedLoginError}
          onSignupRequested={onSignupRequested}
        />
      )
    case 'challenges': {
      const challenges = result.value
      return challenges.challenges.includes(Challenge.PASSWORD) ? (
        <LoginForm
          form={form}
          showLinkedAccounts={showLinkedAccounts}
          federatedLoginError={federatedLoginError}
          onSignupRequested={onSignupRequested}
          wrongPassword
        />
      ) : (
        <ChallengeDialogModal
          requestType="positive"
          challenges={challenges}
          onRequest={credentials =>
            submitCredentials(form.email.value, credentials)
          }
          onClose={reset}
          defaultCredentials={{
            password: form.password.value,
          }}
        />
      )
    }
    case 'redirectUrl':
      return <Redirect to={result.value} reload />
    case 'loginExpired':
      return <LoginExpired />
    default:
      return exhaustiveGuard(result)
  }
}
